/**
 * A blending session — the beers that went into one blend, and the packaging
 * numbers computed from them.
 *
 * This is the record a blender keeps: which barrels, how much of each, the
 * target carbonation, the additions, notes. Derived figures (residual CO₂, TA,
 * pH, priming dose, acid-shock starter) are not stored; they are recomputed
 * from this document by `@beerwright/core`'s `evaluateBlend`.
 *
 * An addition has a kind, and the kind says which numbers the row even has
 * (D-BS1): a `fruit` has a volume and a juice gravity, a `whole-fruit` has a
 * weight and a sugar percent, a `sugar` has a weight and a name from the
 * priming list, a `flavour` has a weight and nothing else, a `hop` has a
 * weight under a variety from the catalog (D-BS13), a `spirit` has a volume
 * and an ABV. That is a discriminated union rather than one wide row
 * with three-quarters of its fields null, because a tincture with a juice
 * gravity is not a thing a blender can mean.
 *
 * `sugar` is weighed rather than measured (D-BS10). Honey and syrups rode on
 * the fruit row until a brewer pointed out that nobody puts a jar of honey in
 * a jug or takes a hydrometer to it: it goes on the scale, and what it does in
 * the bottle follows from which sugar it is. So the row asks for a weight and
 * a name off the same list the priming dose names, and the fermentable
 * fraction comes from that name rather than from a gravity reading. The lift
 * below is untouched by this: a honey row saved as a fruit stays a fruit,
 * because the volume and gravity the brewer typed are still what they typed,
 * and rewriting them into a weight would mean inventing a density. Switching
 * an old row over is the brewer's move, on the page, one select away.
 *
 * `fruit` and `whole-fruit` are two kinds for one fruit (D-BS11), and the page
 * shows them as one kind with two modes — By juice and By weight. The document
 * could have matched the page and carried a mode beside the fields, and then
 * every fruit row would hold a `volumeL`, an `sg`, a `massKg` and a
 * `sugarPercent`, three-quarters of them null and meaning nothing, with the
 * mode as the only thing saying which half to read. That is the wide row this
 * union was built to avoid, so the split stays down here where the numbers
 * are, and the page does the joining up where the brewer is.
 *
 * The old key lifts (D-BS2). The first build stored one `fruit` or `null`; the
 * second stored a `fruits` list; both land on `additions` with `kind: 'fruit'`
 * on every row, in the preprocess below. No schema version moves for a lift —
 * see `journal.ts`'s header on why blends carry none.
 *
 * A blend has a stage and three dates (D-BS3, D-BS4), and both are filled by
 * that same preprocess rather than by a `.default()` on the field. That is not
 * taste: `blendChangesSchema` is this object `.omit`ped and `.partial()`ed, and
 * zod's `.partial()` does not strip a `.default()`. A defaulted `stage` would
 * therefore materialise in every `blend.update` patch that never mentioned one
 * — a rename would file a packaged blend back under `planned`. A preprocess
 * runs on the document and not on a patch, so the fill lands exactly where it
 * belongs. Filling in the preprocess is also what keeps every stored blend
 * parsing without a version moving, the same bargain the additions lift makes.
 *
 * A component may point at a `batchId` or at a `blendId`, never both: a beer in
 * the blend came from the brew log, from another blending session (a barrel's
 * contents, a pull off it), or from nowhere in particular. Either id is
 * provenance only — the numbers on the component are a copy, so deleting the
 * source cannot move a blend that already happened.
 */

import { z } from 'zod';
import {
  batchCellarTaskSchema,
  measuredTempCSchema,
  measuredVolumeSchema,
  specificGravitySchema,
} from './batch';
import {
  documentIdSchema,
  isoDateSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  timestampSchema,
} from './common';

/**
 * Priming sugars the blend document may name. Mirrors core's `PrimingSugar`
 * (see that package's `carbonation.ts`); the values are the ones
 * `evaluateBlend` already accepts.
 */
export const blendPrimingSugarSchema = z.enum([
  'corn-sugar',
  'dextrose',
  'table-sugar',
  'dme',
  'honey',
  'turbinado',
  'maple-syrup',
]);

/** @see blendPrimingSugarSchema */
export type BlendPrimingSugar = z.infer<typeof blendPrimingSugarSchema>;

/** One beer in the blend. Nullable numbers are "not yet measured". */
export const blendComponentSchema = z
  .strictObject({
    /** Stable id within this blend, for the UI's cards and the assistant. */
    componentId: documentIdSchema,
    /**
     * Display name. Empty is allowed: a card typed by hand starts blank, and a
     * name is something you type after the volume.
     */
    name: z.string().max(120),
    /**
     * The batch this card was filled from, or `null` for a beer typed by hand.
     * Never dereferenced — the batch may have been deleted since.
     */
    batchId: documentIdSchema.nullable(),
    /**
     * The blending session this card was filled from, or `null`. Defaulted so
     * a session stored before a blend could hold a blend still parses, and
     * never dereferenced, for the same reason as `batchId`.
     */
    blendId: documentIdSchema.nullable().default(null),
    /** Volume in the blend, **litres**. */
    volumeL: measuredVolumeSchema.nullable(),
    /** Original gravity, SG. */
    og: specificGravitySchema.nullable(),
    /** Final gravity, SG. */
    fg: specificGravitySchema.nullable(),
    /** Sample pH. */
    ph: z.number().finite().min(0).max(14).nullable(),
    /** Colour, **SRM**. */
    srm: nonNegativeSchema.max(200).nullable(),
    /** Highest temperature after attenuation, **°C**. */
    peakTempC: measuredTempCSchema.nullable(),
    /** mL of 0.1 N NaOH to pH 8.2, 25 mL sample. */
    naohMl: nonNegativeSchema.max(1_000).nullable(),
    /** True when the beer sat in a barrel long enough to lose residual CO₂. */
    barrelAged: z.boolean(),
  })
  // A card was filled from one place or from neither, so two provenance ids on
  // one component is a client bug rather than a beer.
  .refine((component) => component.batchId === null || component.blendId === null, {
    message: 'A beer comes from a batch or from a blend, not both.',
    path: ['blendId'],
  });

/** @see blendComponentSchema */
export type BlendComponent = z.infer<typeof blendComponentSchema>;

/** What an addition is, and so which numbers its row carries. */
export const blendAdditionKindSchema = z.enum([
  'fruit',
  'whole-fruit',
  'sugar',
  'flavour',
  'hop',
  'spirit',
]);

/** @see blendAdditionKindSchema */
export type BlendAdditionKind = z.infer<typeof blendAdditionKindSchema>;

/**
 * The two days every addition row carries, whatever kind it is (D-BS12).
 *
 * `addedOn` is load-bearing on a row that brings sugar: fruit tipped in a
 * month before packaging has fermented out, fruit tipped in the week before
 * bottling is priming sugar nobody counted, and core's `evaluateBlend` can
 * only tell those apart if somebody wrote the day down. `removedOn` is
 * record-keeping and nothing else — a dry hop pulled after two days changes
 * what the beer tastes like and no number on this page — but it is the
 * question a blender actually asks the next time they reach for the same hop,
 * so the row carries it.
 *
 * `.default(null)` here is safe where the same default on a top-level field
 * would not be: `blendChangesSchema` is the document object `.partial()`ed,
 * and `.partial()` reaches top-level keys only. A default inside an array
 * element is never materialised into a patch that does not mention the array,
 * so an addition stored before these existed parses as an undated one and
 * nothing is written back onto a blend that never mentioned its additions.
 */
const blendAdditionTimingFields = {
  /** The day it went in, `YYYY-MM-DD`, or `null` when the blender did not say. */
  addedOn: isoDateSchema.nullable().default(null),
  /** The day it came out, or `null`. Recorded, never computed from. */
  removedOn: isoDateSchema.nullable().default(null),
};

/** Anything folded into the blend that is not a beer. Nullable numbers are "not yet". */
export const blendAdditionSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    /** Stable id within this blend, for the UI's rows. */
    additionId: documentIdSchema,
    kind: z.literal('fruit'),
    name: z.string().max(120),
    /** Juice / syrup volume, **litres**. */
    volumeL: measuredVolumeSchema.nullable(),
    /** Juice / syrup specific gravity. */
    sg: specificGravitySchema.nullable(),
    ...blendAdditionTimingFields,
  }),
  z.strictObject({
    additionId: documentIdSchema,
    kind: z.literal('whole-fruit'),
    name: z.string().max(120),
    /** Amount by weight, **kilograms**, fruit and all. */
    massKg: nonNegativeSchema.max(1_000).nullable(),
    /** Sugar as a share of that weight, **percent**. */
    sugarPercent: z.number().finite().min(0).max(100).nullable(),
    /**
     * Which fruit it is, as a `WHOLE_FRUITS` id, or `null` for one the table
     * does not name. Only ever a label for the percent beside it: the maths
     * reads `sugarPercent`, which the brewer may have overwritten, so a row
     * whose id went stale still computes what they typed.
     */
    fruit: z.string().max(40).nullable(),
    ...blendAdditionTimingFields,
  }),
  z.strictObject({
    additionId: documentIdSchema,
    kind: z.literal('sugar'),
    name: z.string().max(120),
    /** Amount by weight, **kilograms**. Honey, sugar and syrup all go on a scale. */
    massKg: nonNegativeSchema.max(1_000).nullable(),
    /** Which sugar it is, from the same list the priming dose is named from. */
    sugar: blendPrimingSugarSchema,
    ...blendAdditionTimingFields,
  }),
  z.strictObject({
    additionId: documentIdSchema,
    kind: z.literal('flavour'),
    name: z.string().max(120),
    /** Amount by weight, **grams**. Oak cubes and a vanilla pod are both weighed. */
    amountG: nonNegativeSchema.max(100_000).nullable(),
    ...blendAdditionTimingFields,
  }),
  z.strictObject({
    additionId: documentIdSchema,
    kind: z.literal('hop'),
    /**
     * The variety, from the catalog or typed by hand (D-BS13).
     *
     * A plain name and no id: the catalog is here to spell "Hallertauer
     * Mittelfrüh" for the blender, not to be dereferenced later. Nothing on
     * this row is computed from the variety — a hop on finished beer is 0 IBU
     * whatever it is — so an id would be a key nothing ever looks up, and a
     * hop the catalog has never heard of has to be typeable anyway.
     */
    name: z.string().max(120),
    /** Amount by weight, **grams**, the measure a dry hop is bought and weighed in. */
    amountG: nonNegativeSchema.max(100_000).nullable(),
    ...blendAdditionTimingFields,
  }),
  z.strictObject({
    additionId: documentIdSchema,
    kind: z.literal('spirit'),
    name: z.string().max(120),
    /** Spirit / tincture volume, **litres**. */
    volumeL: measuredVolumeSchema.nullable(),
    /** Strength of what is poured in, **percent ABV**. */
    abvPercent: z.number().finite().min(0).max(100).nullable(),
    ...blendAdditionTimingFields,
  }),
]);

/** @see blendAdditionSchema */
export type BlendAddition = z.infer<typeof blendAdditionSchema>;

// ---------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------

/**
 * Where a blend is in its life (D-BS3).
 *
 * Ordered as the blender moves through it, which is what the detail page's
 * stepper renders. `blended` is its own stage because the afternoon with the
 * buckets and the weeks in bottle afterwards are different things. `archived`
 * is last and off the happy path — "filed away", reached from the list's row
 * menu, not a fifth thing that happens to the beer. The batch's shape, for the
 * batch's reasons.
 */
export const blendStageSchema = z.enum([
  'planned',
  'blended',
  'conditioning',
  'packaged',
  'archived',
]);

/** @see blendStageSchema */
export type BlendStage = z.infer<typeof blendStageSchema>;

/** Every stage, in stepper order. */
export const BLEND_STAGES: readonly BlendStage[] = blendStageSchema.options;

/** The stages that form the linear planned → packaged progression. */
export const BLEND_PROGRESS_STAGES: readonly BlendStage[] = [
  'planned',
  'blended',
  'conditioning',
  'packaged',
];

/**
 * The stages that carry a date.
 *
 * `planned` does not: the day a blend was planned is its `createdAt`, and a
 * second field saying the same thing is a second field to keep honest.
 */
export const BLEND_DATED_STAGES = ['blended', 'conditioning', 'packaged'] as const;

/** @see BLEND_DATED_STAGES */
export type BlendDatedStage = (typeof BLEND_DATED_STAGES)[number];

/**
 * Whether this stage is one that carries a date.
 *
 * A narrowing predicate rather than a bare `includes`, because the callers
 * that ask are about to index `stageDates` with the answer.
 */
export function isBlendDatedStage(stage: BlendStage): stage is BlendDatedStage {
  return (BLEND_DATED_STAGES as readonly BlendStage[]).includes(stage);
}

/**
 * The day each dated stage was reached, or `null` for "not yet".
 *
 * `YYYY-MM-DD` rather than an instant, as cellar tasks and harvest dates are:
 * a stage is a day in a cellar, not a moment on a clock, and a brewer who
 * types 3 March means the whole of it wherever they are standing.
 */
export const blendStageDatesSchema = z.strictObject({
  blended: isoDateSchema.nullable(),
  conditioning: isoDateSchema.nullable(),
  packaged: isoDateSchema.nullable(),
});

/** @see blendStageDatesSchema */
export type BlendStageDates = z.infer<typeof blendStageDatesSchema>;

/** Three dates, none of them reached yet. */
export function emptyBlendStageDates(): BlendStageDates {
  return { blended: null, conditioning: null, packaged: null };
}

// ---------------------------------------------------------------------------
// What was measured on the blend itself
// ---------------------------------------------------------------------------

/**
 * One thing a hydrometer said about the blend, as opposed to about the beers
 * that went into it (D-BS12).
 *
 * The component cards already carry a gravity each, and until this existed the
 * blend's FG was only ever the volume-weighted mean of them — the blend on the
 * afternoon the buckets were tipped. Every other figure on the page describes
 * the blend months later, once the bugs have eaten the sweeter components down
 * to the driest one's floor: that is what the extra carbonation from leftover
 * extract is, and what the ABV with additions is. Both are predictions about
 * the same vessel, and nothing recorded which of them came true.
 *
 * **A list, for the reason a batch keeps its tastings in one.** A blend sits
 * for months, and a gravity falling 1.012 → 1.008 → 1.004 over a winter is the
 * record: it is how a blender knows the culture is still working, and a gueuze
 * stalled at 1.010 is a different animal from one still dropping. Keeping only
 * the newest number would throw that away, exactly as keeping only the newest
 * tasting would throw away the fact that a beer faded. The arithmetic wants one
 * answer, but that is a reason to *read* one, not to store one: everything
 * derived takes the latest reading, the same way insights takes the most recent
 * scored tasting, and the rest is history the page draws.
 *
 * Both fields are nullable because a row is born the moment the blender presses
 * Add, before they have typed anything into it — the component card's rule.
 */
export const blendReadingSchema = z.strictObject({
  /** Stable id within this blend, for the UI's rows. */
  readingId: documentIdSchema,
  /** Final gravity of the blend itself, SG, or `null` while the row is blank. */
  fg: specificGravitySchema.nullable(),
  /**
   * The day it was read, `YYYY-MM-DD`, or `null`.
   *
   * Not decoration, and the field that orders the list: a reading contains
   * every addition that went in before it, so without a day the fruit's sugar
   * would be counted once in the gravity and again in the ABV. The bridge sorts
   * on this to find the latest, and `evaluateBlend` splits the additions on it.
   */
  on: isoDateSchema.nullable(),
});

/** @see blendReadingSchema */
export type BlendReading = z.infer<typeof blendReadingSchema>;

/** How many readings one blend may hold. */
export const MAX_BLEND_READINGS = 40;

/** An empty reading row. Ids come from the caller. */
export function blankBlendReading(readingId: string): BlendReading {
  return { readingId, fg: null, on: null };
}

/**
 * The reading everything derived is computed from: the latest one that has a
 * gravity, or `undefined` when none of them does.
 *
 * "Latest" is by day, with an undated row sorting oldest — a blender who typed
 * a gravity and no day has still told us something, so it stands until a dated
 * reading overtakes it, and among several undated ones the last row entered
 * wins. Exported because the page shows which row is driving the numbers and
 * the bridge feeds it to core; one definition, so the two cannot disagree.
 */
export function latestBlendReading(readings: readonly BlendReading[]): BlendReading | undefined {
  let latest: BlendReading | undefined;
  for (const reading of readings) {
    if (reading.fg === null) continue;
    if (latest === undefined) {
      latest = reading;
      continue;
    }
    // An undated candidate never displaces a dated one; otherwise later wins,
    // and equal days fall to the row entered last.
    if (reading.on === null) {
      if (latest.on === null) latest = reading;
      continue;
    }
    if (latest.on === null || reading.on >= latest.on) latest = reading;
  }
  return latest;
}

/** How many additions one session may hold. */
export const MAX_BLEND_ADDITIONS = 12;

/** Fields of a blending session, without the legacy `fruit` / `fruits` lift. */
export const blendDocFieldsSchema = z.strictObject({
  id: documentIdSchema,
  name: nameSchema,
  notes: notesSchema,
  /** Target carbonation, **volumes of CO₂**. */
  targetVolumes: z.number().finite().min(0).max(6),
  sugar: blendPrimingSugarSchema,
  /** Volume going to keg, **litres**; `null` means the whole blend is bottled. */
  kegVolumeL: measuredVolumeSchema.nullable(),
  /** Fruit whole or juiced, sugar, flavourings and spirits. Empty means none. */
  additions: z.array(blendAdditionSchema).max(MAX_BLEND_ADDITIONS),
  /**
   * Where this blend is (D-BS3). No `.default()` here, deliberately — see the
   * header: the preprocess fills it, because `.partial()` would carry a
   * default into every `blend.update` patch that never mentioned a stage.
   */
  stage: blendStageSchema,
  /** The day each dated stage was reached (D-BS4). Filled by the preprocess too. */
  stageDates: blendStageDatesSchema,
  /**
   * Every hydrometer reading taken on the blend itself, oldest first
   * (D-BS12). No `.default()`, for `stage`'s reason exactly — see the header.
   */
  readings: z.array(blendReadingSchema).max(MAX_BLEND_READINGS),
  /**
   * Cellar actions the blender put on the calendar themselves (PLAN-UX-PASS
   * E1).
   *
   * **The batch's schema, not a copy of it.** D-BS5 already says the batch's
   * phases and the blend's are the same control drawn twice; a chore list is
   * the same fact again. Pull the oak cubes, rack off the fruit, taste it in a
   * month: a blender's chores have the same six kinds, the same day, and the
   * same `doneAt` tick a batch's do, and two schemas that happened to agree
   * would be two places for a seventh kind to be added.
   *
   * Nothing is derived for a blend, so this list is the whole of what a blend
   * puts on the calendar. A blend's stage dates stay off it, by the park at
   * `PLAN-BLEND-STAGES.md:600`.
   *
   * No `.default([])`, for the reason `stage` and `readings` carry none — see
   * the header. The preprocess below fills it, so a session stored before this
   * field existed still parses as one with no chores, which is the truth for
   * every one of them.
   */
  cellarTasks: z.array(batchCellarTaskSchema),
  components: z.array(blendComponentSchema).max(20),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

/**
 * Lift a session saved with `fruit` or `fruits` onto `additions` (D-BS2).
 *
 * Three shapes have been stored: one `fruit` or `null`, then a `fruits` list,
 * now `additions`. A legacy row is a fruit by definition, so it gains
 * `kind: 'fruit'`; ids that were never minted get `fr-1`, `fr-2`, … so a
 * document that round-trips through JSON still has stable row keys. The old
 * keys are deleted rather than left beside the new one, because the field
 * schema is strict and would refuse them.
 *
 * Exported because `blend.update`'s `changes` needs the same lift: the command
 * log is replayed from the beginning, and an update written last month names
 * `fruits`.
 */
export function liftBlendAdditions(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = { ...(raw as Record<string, unknown>) };
  const single = rec.fruit;
  const list = rec.fruits;
  delete rec.fruit;
  delete rec.fruits;
  const rows = Array.isArray(rec.additions)
    ? rec.additions
    : Array.isArray(list)
      ? list
      : single === null || single === undefined
        ? []
        : [single];
  rec.additions = rows.map((item, index) => {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) return item;
    const row = item as Record<string, unknown>;
    const additionId = typeof row.additionId === 'string' ? {} : { additionId: `fr-${index + 1}` };
    const kind = typeof row.kind === 'string' ? {} : { kind: 'fruit' };
    return { ...row, ...additionId, ...kind };
  });
  return rec;
}

/**
 * Bring a stored blending session up to the schema above: lift its additions,
 * and give it a stage and three dates if it was saved before it had them.
 *
 * The fill is here rather than on the fields because this runs on a whole
 * document and `blendChangesSchema` runs on a patch — see the header. It is
 * "absent means planned", never "correct the value": a session that already
 * says `packaged` keeps saying it, and a malformed one is refused below rather
 * than quietly reset.
 */
function liftBlendDoc(raw: unknown): unknown {
  const lifted = liftBlendAdditions(raw);
  if (typeof lifted !== 'object' || lifted === null || Array.isArray(lifted)) return lifted;
  const rec = lifted as Record<string, unknown>;
  return {
    ...rec,
    stage: rec.stage === undefined ? 'planned' : rec.stage,
    stageDates: rec.stageDates === undefined ? emptyBlendStageDates() : rec.stageDates,
    readings: rec.readings === undefined ? [] : rec.readings,
    cellarTasks: rec.cellarTasks === undefined ? [] : rec.cellarTasks,
  };
}

/** A persisted blending session. */
export const blendDocSchema = z.preprocess(liftBlendDoc, blendDocFieldsSchema);

/** @see blendDocSchema */
export type BlendDoc = z.infer<typeof blendDocFieldsSchema>;

/** Carbonation a blank blend starts at — mixed-fermentation / gueuze territory. */
export const DEFAULT_BLEND_TARGET_VOLUMES = 3;

/**
 * A blank blending session: named, and otherwise empty.
 *
 * Pure — `id`, `name` and both timestamps come from the caller.
 */
export function blankBlendDoc(args: {
  readonly id: string;
  readonly name: string;
  readonly timestamp: number;
  readonly components?: readonly BlendComponent[];
}): BlendDoc {
  return {
    id: args.id,
    name: args.name,
    notes: '',
    targetVolumes: DEFAULT_BLEND_TARGET_VOLUMES,
    sugar: 'table-sugar',
    kegVolumeL: null,
    additions: [],
    stage: 'planned',
    stageDates: emptyBlendStageDates(),
    readings: [],
    cellarTasks: [],
    components: args.components === undefined ? [] : [...args.components],
    createdAt: args.timestamp,
    updatedAt: args.timestamp,
  };
}

/** An empty component card. Ids come from the caller. */
export function blankBlendComponent(componentId: string): BlendComponent {
  return {
    componentId,
    name: '',
    batchId: null,
    blendId: null,
    volumeL: null,
    og: null,
    fg: null,
    ph: null,
    srm: null,
    peakTempC: null,
    naohMl: null,
    barrelAged: false,
  };
}

/** An empty addition row of the kind asked for. Ids come from the caller. */
export function blankBlendAddition(additionId: string, kind: BlendAdditionKind): BlendAddition {
  // Undated, whatever the kind: a row is created the moment the blender picks
  // the kind, which is often before they have decided when it goes in, and a
  // date defaulted to today would be a claim nobody made (D-BS12).
  const undated = { addedOn: null, removedOn: null };
  switch (kind) {
    case 'fruit':
      return { additionId, kind, name: '', volumeL: null, sg: null, ...undated };
    // All three null: the fruit is what the brewer picks first, and picking it
    // is what fills the percent, so a default percent here would be a figure
    // standing in for a fruit nobody has named.
    case 'whole-fruit':
      return {
        additionId,
        kind,
        name: '',
        massKg: null,
        sugarPercent: null,
        fruit: null,
        ...undated,
      };
    case 'sugar':
      // Honey rather than table sugar: a blender reaching for this row is
      // usually holding a jar of it, and the priming select below is where
      // sucrose is the obvious default.
      return { additionId, kind, name: '', massKg: null, sugar: 'honey', ...undated };
    case 'flavour':
    case 'hop':
      return { additionId, kind, name: '', amountG: null, ...undated };
    case 'spirit':
      return { additionId, kind, name: '', volumeL: null, abvPercent: null, ...undated };
  }
}
